/**
 * ITDEVELOP-3118: Temporary source of truth for LMS course content.
 *
 * Course content is bundled in lms-course.json rather than fetched from the legacy database. The course ID is
 * deliberately injected at runtime because assignments and completion records still use the backend-provided ID.
 */
import sourceCourseJson from './lms-course.json';
import type {
    Content,
    Course,
    Image,
    Paragraph,
    Question,
    QuestionOption,
    Quiz,
    Section,
    Video,
} from '@/stores/store-interfaces';

interface SourceCourse {
    title: string;
    description: string;
    duration: number;
    sections: SourceSection[];
}

interface SourceSection {
    title: string;
    order: number;
    duration: number;
    contents: SourceContent[];
    quizes: SourceQuiz[];
}

interface SourceContent {
    content_type_id: string;
    title: string;
    description: string;
    order: number;
    duration?: number;
    paragraphs?: SourceParagraph[];
    images?: SourceMedia[];
    videos?: SourceMedia[];
}

interface SourceParagraph {
    paragraph: string;
}

interface SourceMedia {
    file_id: string;
    label: string;
    type: string;
}

interface SourceQuiz {
    title: string;
    description: string;
    order: number;
    questions: SourceQuestion[];
}

interface SourceQuestion {
    title: string;
    description: string;
    order: number;
    options: SourceQuestionOption[];
}

interface SourceQuestionOption {
    question: string;
    correct: boolean;
}

const sourceCourse: SourceCourse = sourceCourseJson;
const metadata = {
    created_at: 0,
    modified_at: 0,
    created_by: null,
    modified_by: null,
};

export function getHardcodedCourse(courseId: string): Course {
    return {
        ...metadata,
        course_id: courseId,
        title: sourceCourse.title,
        description: sourceCourse.description,
        duration: sourceCourse.duration,
        sections: sourceCourse.sections.map((section, sectionIndex) => mapSection(section, courseId, sectionIndex)),
    };
}

function mapSection(section: SourceSection, courseId: string, sectionIndex: number): Section {
    const sectionId = `hardcoded-section-${sectionIndex}`;

    return {
        ...metadata,
        section_id: sectionId,
        course_id: courseId,
        title: section.title,
        duration: section.duration,
        order: section.order,
        contents: section.contents.map((content, contentIndex) => mapContent(content, sectionId, contentIndex)),
        quizes: section.quizes.map((quiz, quizIndex) => mapQuiz(quiz, sectionId, quizIndex)),
    };
}

function mapContent(content: SourceContent, sectionId: string, contentIndex: number): Content {
    const contentId = `${sectionId}-content-${contentIndex}`;

    return {
        ...metadata,
        content_id: contentId,
        section_id: sectionId,
        content_type_id: content.content_type_id,
        title: content.title,
        description: content.description,
        duration: content.duration ?? 0,
        order: content.order,
        type: {
            ...metadata,
            content_type_id: content.content_type_id,
            name: getContentTypeName(content),
        },
        images: (content.images ?? []).map((image, imageIndex) => mapImage(image, contentId, imageIndex)),
        videos: (content.videos ?? []).map((video, videoIndex) => mapVideo(video, contentId, videoIndex)),
        paragraphs: (content.paragraphs ?? []).map((paragraph, paragraphIndex) =>
            mapParagraph(paragraph, contentId, paragraphIndex),
        ),
    };
}

function getContentTypeName(content: SourceContent): string {
    if (content.videos?.length) return 'video';
    if (content.images?.length) return 'image';
    return 'text';
}

function mapImage(image: SourceMedia, contentId: string, imageIndex: number): Image {
    return {
        ...metadata,
        ...image,
        content_image_id: `${contentId}-image-${imageIndex}`,
        content_id: contentId,
    };
}

function mapVideo(video: SourceMedia, contentId: string, videoIndex: number): Video {
    return {
        ...metadata,
        ...video,
        content_video_id: `${contentId}-video-${videoIndex}`,
        content_id: contentId,
    };
}

function mapParagraph(paragraph: SourceParagraph, contentId: string, paragraphIndex: number): Paragraph {
    return {
        ...metadata,
        ...paragraph,
        content_paragraph_id: `${contentId}-paragraph-${paragraphIndex}`,
        content_id: contentId,
    };
}

function mapQuiz(quiz: SourceQuiz, sectionId: string, quizIndex: number): Quiz {
    const quizId = `${sectionId}-quiz-${quizIndex}`;

    return {
        ...metadata,
        quiz_id: quizId,
        section_id: sectionId,
        title: quiz.title,
        description: quiz.description,
        order: quiz.order,
        questions: quiz.questions.map((question, questionIndex) => mapQuestion(question, quizId, questionIndex)),
    };
}

function mapQuestion(question: SourceQuestion, quizId: string, questionIndex: number): Question {
    const questionId = `${quizId}-question-${questionIndex}`;

    return {
        ...metadata,
        question_id: questionId,
        quiz_id: quizId,
        title: question.title,
        description: question.description,
        order: question.order,
        options: question.options.map((option, optionIndex) => mapQuestionOption(option, questionId, optionIndex)),
    };
}

function mapQuestionOption(
    option: SourceQuestionOption,
    questionId: string,
    optionIndex: number,
): QuestionOption {
    return {
        ...metadata,
        question_option_id: `${questionId}-option-${optionIndex}`,
        question_id: questionId,
        question: option.question,
        correct: option.correct,
    };
}
